<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-xl">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-amaze-coral sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white font-bold">
                                                Link Supplier Invoice to Projects
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-amaze-coral rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-white">
                                                Get started by filling in the information below to create a new
                                                supplier invoice.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="mt-6 grid grid-cols-2 gap-4">
                                                <div class="col-span-2">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">Project Information</h3>
                                                </div>
                                                <div class="col-span-2">
                                                    <label for="invoice"
                                                           class="block text-sm text-gray-900 required">
                                                        Related Project
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="invoice" v-model="form.project_id"
                                                                :class="{'border-red-500': form.errors.project_id}"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value selected
                                                                    v-html="'Select a project'"></option>
                                                            <option v-for="(project,index) in projects"
                                                                    v-html="project.reference + ' - ' + project.title + ' (' + project.friendly_starts_at + ')'"
                                                                    :value="project.id"></option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div class="col-span-2">
                                                    <label for="gbp_amount"
                                                           class="block text-sm text-gray-900 required">
                                                        GBP Amount
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="transaction_type_title" v-model="form.amount"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.amount}"
                                                        />
                                                    </div>
                                                </div>

                                                <div class="col-span-2 flex justify-end">
                                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                                            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-500">
                                                        <span v-if="loading === true">Saving..</span>
                                                        <span v-else>Add Related Project</span>
                                                    </button>
                                                </div>

                                                <h2 class="text-xl text-bold col-span-2">Currently Linked Projects</h2>
                                                <div
                                                    class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg relative col-span-2">

                                                    <table class="min-w-full divide-y divide-gray-200">
                                                        <thead class="bg-gray-50">
                                                        <tr>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Project
                                                            </th>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Amount
                                                            </th>
                                                            <th scope="col" class="relative px-6 py-3">
                                                                <span class="sr-only">Edit</span>
                                                            </th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        <tr v-if="related_projects.length" v-for="(related_project, index) in related_projects"
                                                            :class="{'bg-gray-100': index % 2}">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ related_project.reference }} - {{ related_project.title }}<br />({{ related_project.friendly_starts_at }})
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                £{{ number_format(parseFloat(related_project.pivot.amount).toFixed(2)) }}
                                                            </td>
                                                            <td class="px-6 w-1/5 py-4 whitespace-nowrap text-right text-sm">

                                                                <a @click="deleteRelatedProject(related_project)" v-if="related_projects.length > 1"
                                                                   class="text-red-600 hover:text-red-900 float-right hover:cursor-pointer">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                                                                         viewBox="0 0 24 24" class="w-4 h-4"
                                                                         stroke="currentColor">
                                                                        <path stroke-linecap="round"
                                                                              stroke-linejoin="round" stroke-width="2"
                                                                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                                    </svg>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                        <tr :class="[(parseFloat(linkedInvoiceTotal) == parseFloat(supplierInvoiceTotal)) ? 'bg-green-100' : 'bg-red-100']" v-if="related_projects.length > 0">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                                Total
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                                £{{ number_format(parseFloat(linkedInvoiceTotal).toFixed(2)) }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">

                                                            </td>
                                                        </tr>
                                                        <tr class="bg-gray-200">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                                Invoice Total
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                                £{{ number_format(parseFloat(supplierInvoiceTotal).toFixed(2)) }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">

                                                            </td>
                                                        </tr>
                                                        <tr v-if="related_projects.length === 0">
                                                            <td colspan="3" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                There are no related projects for this supplier invoice.
                                                            </td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {useForm, usePage} from '@inertiajs/vue3'

import SlideOutErrors from "@/Components/SlideOutErrors.vue";
import {emitter} from "@/eventHub";

export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        XIcon,
    },
    setup(prop, context) {

        const show = ref(false)

        const showErrors = ref(false)

        const updating = false;

        const loading = ref(null);

        const project = usePage().props.project;

        const linkedInvoiceTotal = ref(0);

        const supplierInvoiceTotal = ref(0);

        const related_projects = ref([]);

        const projects = ref([]);

        const form = useForm({
            id: null,
            project_id: "",
            supplier_invoice_id: "",
            amount: null
        })

        function submit() {
            this.form.post(route('frontend.project.supplier-invoice.invoice.related-project.store', [usePage().props.project.id, this.form.supplier_invoice_id]), {
                onSuccess: page => {
                    this.show = false;
                    this.showErrors = false;
                    this.form.reset();
                },
                onError: errors => {
                    this.showErrors = true;
                }
            });
        }

        function close() {
            this.form.reset();
            this.form.clearErrors();
            this.updating = false;
            this.show = false;
        }

        function open(supplier_invoice) {
            this.loading = true;
            this.fetchOrganisationProjects();

            this.loading = false;
            this.related_projects = supplier_invoice.related_projects;
            this.linkedInvoiceTotal = supplier_invoice.total_project_linked_amount;
            this.supplierInvoiceTotal = supplier_invoice.gbp_amount;
            this.form.supplier_invoice_id = supplier_invoice.id;
            this.form.clearErrors();
            this.show = true;
        }

        const emitter = emitter;

        return {
            show,
            form,
            submit,
            updating,
            close,
            open,
            showErrors,
            emitter,
            loading,
            project,
            projects,
            related_projects,
            linkedInvoiceTotal,
            supplierInvoiceTotal
        }
    },
    methods: {
        fetchOrganisationProjects () {
            this.loading = true;
            axios.get(route('frontend.project.get_supplier_invoices_projects_config', {project: this.project.id, _query: {supplier_invoice_id: this.form.supplier_invoice_id}})).then(response => {
                this.projects = response.data.projects;
                this.loading = false;
            });
        },
        deleteRelatedProject(related_project) {
            this.$swal.fire({
                title: 'Remove Related Project',
                html: "Are you sure you want to move the relation between this project and the supplier invoice?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No',
                stopKeydownPropagation: true,
                keydownListenerCapture: true
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        // Trigger the loader
                        this.loading = true;
                        // Delete the relation
                        Inertia.delete(route('frontend.project.supplier-invoice.invoice.delete-linked-project', [this.project.id, this.form.supplier_invoice_id, related_project.pivot.id]), {
                            preserveScroll: true,
                            onSuccess: (page) => {
                                this.loading = false;
                                this.close();
                            },
                            onError: (page) => {
                                this.loading = false
                                this.showErrors = true;
                            }
                        });
                    }
                });
        },
        number_format(nStr) {
            nStr += '';
            let x = nStr.split('.');
            let x1 = x[0];
            let x2 = x.length > 1 ? '.' + x[1] : '';
            let rgx = /(\d+)(\d{3})/;
            while (rgx.test(x1)) {
                x1 = x1.replace(rgx, '$1' + ',' + '$2');
            }
            return x1 + x2;
        },
    },
    mounted() {
        emitter.on('openLinkSupplierInvoiceProjectSlideout', (supplier_invoice) => {
            this.open(supplier_invoice);
        });
    }
}
</script>
